/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-02 16:52:11
 **/

import { PropsWithChildren, useEffect, useState } from "react";
import LottiePlayer from "@rsupport/lottieplayer";

import { Properties } from "@/frameworks/libraries/properties";
import { Button } from "@/frameworks/designStuff/components";

import { colors } from "@/frameworks/core/styles";
import { usePhoneCall } from "@/frameworks/core/hooks";

import type { CommandsProps } from "./CommandBar";
import { Configure } from "@/frameworks/libraries/utilities";

const LOGTAG = "#IdleCommands#";

export default function IdleCommands({
	callID,
	lottieOptions,
	overCallees,
	isGroupCallHistory,
	onAddUser
}: PropsWithChildren<CommandsProps>) {
	const phoneCall = usePhoneCall();
	const [mediaState, setMediaState] = useState(phoneCall.default_user_media_info);
	const callees = phoneCall.state.callees[0];

	useEffect(() => {
		setMediaState(phoneCall.default_user_media_info);
	}, [callees[0]]);

	useEffect(() => {
		Properties.property = {
			callDeviceState: {
				audio: mediaState.audio,
				video: mediaState.video
			}
		};
	}, [mediaState]);

	const handleLocalMediaState = (ev: React.MouseEvent<HTMLButtonElement>) => {
		const { id } = ev.currentTarget;
		switch (id) {
			case "video":
			case "audio": {
				setMediaState((prev) => ({
					...prev,
					[id]: prev[id] === "on" ? "off" : "on"
				}));
				break;
			}
		}
	};

	return (
		<>
			<li>
				<Button.Icon
					id="video"
					name={mediaState.video === "on" ? "CamOn24" : "CamOff24"}
					size="Xlarge"
					onClick={handleLocalMediaState}
					filled={mediaState.video === "on" ? "neutral_secondary" : "secondary"}
					style={{ border: `1px solid ${colors.neutral_50_20}` }}
				/>
			</li>
			<li>
				<Button.Icon
					id="audio"
					name={mediaState.audio === "on" ? "MicOn24" : "MicOff24"}
					size="Xlarge"
					onClick={handleLocalMediaState}
					filled={mediaState.audio === "on" ? "neutral_secondary" : "secondary"}
					style={{ border: `1px solid ${colors.neutral_50_20}` }}
				/>
			</li>
			<li>
				<Button
					filled="transparent"
					disabled={true}
					style={{
						border: `1px solid ${colors.neutral_50_20}`,
						borderRadius: "100%",
						width: 36,
						height: 36,
						opacity: 0.4
					}}
				>
					<LottiePlayer isStopped={true} isPaused={true} options={lottieOptions} height={24} width={24} />
				</Button>
			</li>
			{!isGroupCallHistory && (
				<li>
					<Button.Icon
						name="CallAdd24"
						filled={overCallees ? "transparent" : "neutral"}
						size="Xlarge"
						disabled={false}
						onClick={onAddUser}
						style={{ border: `1px solid ${colors.neutral_50_20}` }}
					/>
				</li>
			)}
		</>
	);
}
